import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { ECOSYSTEM } from "../../data/homeContent";

const easeOut = [0.22, 1, 0.36, 1];

const partners = ECOSYSTEM.partners;
const ventures = ECOSYSTEM.ventures;

// Node home positions on a ring, expressed as percentages of the square
// container (so the whole field scales down gracefully on mobile). During
// rotation the anchors are moved continuously along the same circle.
const ringPositions = (count, radiusPct, offsetDeg) =>
  Array.from({ length: count }, (_, i) => {
    const deg = offsetDeg + (360 / count) * i;
    const rad = (deg * Math.PI) / 180;
    return {
      r: radiusPct,
      deg,
      left: 50 + radiusPct * Math.cos(rad),
      top: 50 + radiusPct * Math.sin(rad),
      dx: Math.cos(rad),
      dy: Math.sin(rad),
    };
  });

const PARTNER_POS = ringPositions(partners.length, 23, -90);
const VENTURE_POS = ringPositions(ventures.length, 38, -90);

const TeamCard = ({ p, i }) => (
  <motion.article
    data-testid={`team-${i}`}
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay: i * 0.12, ease: easeOut }}
    className="flex items-center gap-5 rounded-3xl glass hover:border-[#C9A24B]/40 transition-all duration-500 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.9)] p-6 lg:p-7"
  >
    <div className="relative shrink-0 w-24 h-24 lg:w-28 lg:h-28 rounded-full overflow-hidden ring-1 ring-[#C9A24B]/50">
      <img
        src={p.img}
        alt={p.name}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
    </div>
    <div className="flex-1 min-w-0">
      <h3 className="font-display text-2xl md:text-[1.65rem] leading-tight text-cream">{p.name}</h3>
      <p className="text-[15px] md:text-base text-[#E7CC86] leading-snug mt-1">{p.role}</p>
      <p className="text-cream/60 text-[15px] leading-relaxed mt-3">{p.bio}</p>
    </div>
  </motion.article>
);

// One orbiting body. Position is driven by the parent via left/top writes
// (layout-based — hit-testing and responsiveness always correct), while the
// entrance is a pure CSS keyframe fade/scale on the button itself with
// `backwards` fill: during the stagger delay the "from" state applies, after
// the animation the button returns to its natural (visible) state. Nodes are
// NEVER gated behind JS — worst case they appear instantly, fully visible.
const OrbitNode = ({ pos, delay, label, selected, onSelect, onLeave, onPin, testid, children, sizeClass, setRef }) => (
  <div
    ref={setRef}
    className="absolute z-10"
    style={{ left: `${pos.left}%`, top: `${pos.top}%` }}
  >
    <button
      type="button"
      data-testid={testid}
      aria-label={label}
      aria-pressed={selected}
      onMouseEnter={onSelect}
      onMouseLeave={onLeave}
      onFocus={onSelect}
      onBlur={onLeave}
      onClick={onPin}
      style={{ animationDelay: `${delay}s` }}
      className={`gme-node-in absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center rounded-full ring-1 bg-[#0C1220]/90 backdrop-blur transition-all duration-300 shadow-[0_12px_32px_-14px_rgba(0,0,0,0.85)] ${sizeClass} ${
        selected
          ? "ring-[#E7CC86]/90 scale-110 bg-[#17203a] shadow-[0_0_34px_-6px_rgba(231,204,134,0.5)]"
          : "ring-[#C9A24B]/45 hover:ring-[#E7CC86]/80 hover:bg-[#151d33] hover:scale-[1.08]"
      }`}
    >
      {children}
    </button>
  </div>
);

const DetailPanel = ({ active }) => {
  const item = active ? (active.type === "partner" ? partners[active.index] : ventures[active.index]) : null;
  return (
    <div
      data-testid="orbit-detail"
      className="glass relative rounded-2xl p-7 md:p-9 min-h-[15rem] md:min-h-[17rem] flex flex-col justify-center overflow-hidden"
    >
      <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-32 h-32 radial-glow blur-2xl pointer-events-none" />
      <div aria-live="polite" className="relative">
        {item ? (
          <>
            <div className="flex items-center gap-3 mb-5">
              <span className="font-display text-3xl text-[#C9A24B]/70">
                {String(active.index + 1).padStart(2, "0")}
              </span>
              <span className="gold-hairline flex-1" aria-hidden="true" />
              <span className="text-[10px] tracking-[0.28em] uppercase text-[#E7CC86]/70">
                {active.type === "partner" ? "Partner Organization" : "Venture & Body of Work"}
              </span>
            </div>
            <h3 className="font-display text-3xl md:text-4xl leading-tight text-cream">{item.name}</h3>
            <p className="mt-4 text-base md:text-lg text-cream/60 leading-relaxed">{item.note}</p>
            {item.to && (
              <Link
                to={item.to}
                data-testid="orbit-detail-link"
                className="mt-6 inline-flex items-center gap-2 text-[#E7CC86] font-semibold hover:gap-3 transition-all w-fit"
              >
                Visit {item.name} <ArrowUpRight size={18} />
              </Link>
            )}
          </>
        ) : (
          <>
            <p className="text-[10px] tracking-[0.28em] uppercase text-[#E7CC86]/70 mb-5">The Field</p>
            <p className="font-serif italic text-xl md:text-2xl leading-relaxed text-cream/70">
              Hover or tap a node to see who carries the work.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const GravitationalField = ({ active, pinned, setActive, setPinned }) => {
  const reduce = useReducedMotion();
  const nodeRefs = useRef({});
  const pausedRef = useRef(false);

  const setNodeRef = (key) => (el) => {
    if (el) nodeRefs.current[key] = el;
  };

  // Deterministic rotation: one rAF loop, timestamp-based angles, per-node
  // base offsets (deg). Runs everywhere framer does, with zero dependence on
  // IntersectionObserver, HMR state, or animation-playback internals.
  useEffect(() => {
    if (reduce) return;
    let start = null;
    let raf = 0;
    const tick = (ts) => {
      if (start === null) start = ts;
      const elapsed = (ts - start) / 1000;
      if (!pausedRef.current) {
        const step = (positions, keys, duration) => {
          const a = (elapsed / duration) * 360;
          for (let i = 0; i < positions.length; i++) {
            const el = nodeRefs.current[keys[i]];
            if (!el) continue;
            const theta = (positions[i].deg + a) * (Math.PI / 180);
            el.style.left = `${50 + positions[i].r * Math.cos(theta)}%`;
            el.style.top = `${50 + positions[i].r * Math.sin(theta)}%`;
          }
        };
        step(PARTNER_POS, partners.map((_, i) => `partner-${i}`), 100);
        step(VENTURE_POS, ventures.map((_, i) => `venture-${i}`), 160);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reduce]);

  const pause = () => { pausedRef.current = true; };
  const resume = () => { pausedRef.current = false; };
  const resetField = () => { setPinned(false); setActive(null); };
  const leaveField = () => { if (!pinned) setActive(null); };

  return (
    <div
      className="orbit-field relative w-[min(660px,100%)] mx-auto aspect-square"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
    >
      {/* Ring guide circles */}
      <svg viewBox="0 0 100 100" aria-hidden="true" className="absolute inset-0 w-full h-full pointer-events-none">
        <circle cx="50" cy="50" r="23" fill="none" stroke="#C9A24B" strokeOpacity="0.38" strokeWidth="0.35" strokeDasharray="0.1 2.6" strokeLinecap="round" />
        <circle cx="50" cy="50" r="38" fill="none" stroke="#C9A24B" strokeOpacity="0.3" strokeWidth="0.35" strokeDasharray="0.1 2.6" strokeLinecap="round" />
      </svg>

      {/* Decorative outer halo — drifts slowly in the opposite direction */}
      {!reduce && (
        <div className="absolute inset-0 gme-orbit-spin pointer-events-none" style={{ animationDuration: "220s" }}>
          <svg viewBox="0 0 100 100" aria-hidden="true" className="absolute inset-0 w-full h-full">
            <circle cx="50" cy="50" r="46.5" fill="none" stroke="#C9A24B" strokeOpacity="0.14" strokeWidth="0.28" strokeDasharray="1.4 3.4" strokeLinecap="round" />
          </svg>
        </div>
      )}

      <p aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 top-[25.5%] hidden sm:block text-[10px] tracking-[0.3em] uppercase text-[#E7CC86]/55 pointer-events-none">
        Partner Organizations
      </p>
      <p aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 top-[91%] hidden sm:block text-[10px] tracking-[0.3em] uppercase text-[#E7CC86]/55 pointer-events-none">
        Ventures &amp; Bodies of Work
      </p>

      {/* Inner ring — partners */}
      <div className="absolute inset-0">
        {partners.map((p, i) => {
          const pos = PARTNER_POS[i];
          const selected = active && active.type === "partner" && active.index === i;
          return (
            <OrbitNode
              key={p.name}
              pos={pos}
              delay={0.3 + i * 0.12}
              label={`${p.name} — ${p.note}`}
              selected={selected}
              onSelect={() => { if (!pinned) setActive({ type: "partner", index: i }); }}
              onLeave={leaveField}
              onPin={() => { setPinned(true); setActive({ type: "partner", index: i }); }}
              testid={`partner-${i}`}
              sizeClass="w-20 h-20 md:w-24 md:h-24"
              setRef={setNodeRef(`partner-${i}`)}
            >
              <span className="font-display text-[9px] md:text-[10px] leading-[1.15] text-center px-2 text-[#E7CC86]">{p.name}</span>
            </OrbitNode>
          );
        })}
      </div>

      {/* Outer ring — ventures */}
      <div className="absolute inset-0">
        {ventures.map((v, i) => {
          const pos = VENTURE_POS[i];
          const selected = active && active.type === "venture" && active.index === i;
          return (
            <OrbitNode
              key={v.name}
              pos={pos}
              delay={0.42 + i * 0.09}
              label={`${v.name} — ${v.note}`}
              selected={selected}
              onSelect={() => { if (!pinned) setActive({ type: "venture", index: i }); }}
              onLeave={leaveField}
              onPin={() => { setPinned(true); setActive({ type: "venture", index: i }); }}
              testid={`venture-${i}`}
              sizeClass="w-12 h-12 md:w-14 md:h-14"
              setRef={setNodeRef(`venture-${i}`)}
            >
              <span className="font-display text-lg md:text-xl text-[#E7CC86]">{String(i + 1).padStart(2, "0")}</span>
            </OrbitNode>
          );
        })}
      </div>

      {/* Nucleus — Gary */}
      <button
        type="button"
        data-testid="orbit-nucleus"
        aria-label="Gary Malkin — founder and gravitational center. Click to return to the field overview."
        onClick={resetField}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex items-center justify-center group/nuc cursor-pointer"
      >
        <div className="relative flex flex-col items-center">
          <div className="absolute -inset-7 radial-glow-strong blur-2xl animate-pulse-glow pointer-events-none" />
          <div className="relative w-24 h-24 md:w-28 md:h-28 rounded-full ring-2 ring-[#C9A24B]/60 overflow-hidden bg-[#0B1020] shadow-[0_0_0_1px_rgba(201,162,75,0.4),0_0_44px_-6px_rgba(201,162,75,0.45)] transition-transform duration-500 group-hover/nuc:scale-[1.06]">
            <img
              src={ECOSYSTEM.people[0].img}
              alt="Gary Malkin"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover object-top"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
          <p className="mt-3.5 text-[10px] tracking-[0.28em] uppercase text-[#E7CC86]/70 text-center">
            Founder &amp; Visionary
          </p>
        </div>
      </button>
    </div>
  );
};

export const Ecosystem = () => {
  const [active, setActive] = useState(null);
  const [pinned, setPinned] = useState(false);

  return (
    <section id="ecosystem" data-testid="ecosystem" className="relative py-24 md:py-32 overflow-hidden grain">
      <div className="aurora-weave slow" />
      <div className="line-weave" />
      <div className="absolute -top-24 left-0 w-[40rem] h-[40rem] radial-glow blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8">
        {/* ── Beat 1 · Header ── */}
        <div className="max-w-4xl mx-auto text-center mb-16 md:mb-24">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-sm tracking-[0.24em] uppercase text-[#E7CC86]/80 mb-5"
          >
            {ECOSYSTEM.eyebrow}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.06, ease: easeOut }}
            className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-cream"
          >
            No meaningful transformation <span className="text-gold">happens alone.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.16, ease: easeOut }}
            className="mt-7 text-lg text-cream/65 max-w-2xl mx-auto leading-relaxed"
          >
            {ECOSYSTEM.body}
          </motion.p>
        </div>

        {/* ── Beat 2 · The Core Team ── */}
        <p className="text-sm tracking-[0.24em] uppercase text-[#E7CC86]/70 mb-7 text-center">The Core Team</p>
        <div className="grid md:grid-cols-2 gap-5 lg:gap-7 items-stretch">
          {ECOSYSTEM.people.map((p, i) => (
            <TeamCard key={p.name} p={p} i={i} />
          ))}
        </div>

        {/* ── Beat 3 · The Gravitational Field ── */}
        <div className="mt-24 md:mt-32 grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] gap-12 lg:gap-16 items-center">
          <div className="relative">
            <div className="absolute -inset-10 glow-bronze blur-3xl pointer-events-none" />
            <div className="absolute -top-16 -left-16 w-72 h-72 glow-plum blur-3xl pointer-events-none" />

            <GravitationalField active={active} pinned={pinned} setActive={setActive} setPinned={setPinned} />

            <p className="mt-6 text-center text-xs tracking-[0.2em] uppercase text-cream/40">
              The orbit pauses when you lean in.
            </p>
          </div>

          <DetailPanel active={active} />
        </div>

        {/* ── Closing · the gravitational center ── */}
        <div className="mt-20 md:mt-28 max-w-3xl mx-auto text-center">
          <div className="gold-hairline max-w-[7rem] mx-auto mb-8" aria-hidden="true" />
          <motion.p
            data-testid="ecosystem-gravitational"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: easeOut }}
            className="font-display text-2xl md:text-4xl leading-[1.25] text-cream/90 [text-wrap:balance]"
          >
            Gary is the <span className="text-gold">gravitational center</span>. But resonance happens in <span className="text-gold">relationship</span>.
          </motion.p>
        </div>
      </div>
    </section>
  );
};
